export type PolicyOrderEntryLike = {
  id: string | null;
  name: string;
};

export type CachedPolicyLike = {
  id?: string;
  name?: string;
};

/**
 * Builds the complete ordered policy ID list for SetScalingPoliciesOrder.
 * Prefer create-step IDs and name matches from the page-cluster cache; only
 * accept entry.id when it exists in that cache (never trust foreign agent IDs).
 * Unordered cached policies are appended at the end.
 */
export function buildOrderedPolicyIds(
  policyOrder: PolicyOrderEntryLike[],
  options: {
    createdPolicyIds?: Record<string, string>;
    cachedPolicies?: CachedPolicyLike[] | null;
  } = {}
): string[] {
  const createdPolicyIds = options.createdPolicyIds ?? {};
  const policies = options.cachedPolicies ?? [];
  const cachedIds = new Set(
    policies.map((p) => p.id).filter((id): id is string => Boolean(id))
  );
  const orderedNames = new Set(policyOrder.map((e) => e.name));

  const ordered = policyOrder
    .map((e) => {
      if (e.name && createdPolicyIds[e.name]) return createdPolicyIds[e.name];
      const fromCache = policies.find((p) => p.name === e.name)?.id;
      if (fromCache) return fromCache;
      if (e.id && cachedIds.has(e.id)) return e.id;
      return undefined;
    })
    .filter((id): id is string => Boolean(id));

  const orderedIds = new Set(ordered);

  const remaining = policies
    .filter(
      (p) =>
        Boolean(p.id) &&
        Boolean(p.name) &&
        !orderedNames.has(p.name!) &&
        !orderedIds.has(p.id!)
    )
    .map((p) => p.id)
    .filter((id): id is string => Boolean(id));

  return [...new Set([...ordered, ...remaining])];
}
